import { c as _c } from "react/compiler-runtime";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn, cva } from "../utils";
import { Icon } from "./icon";
function Empty(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "empty"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
function EmptyHeader(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("flex max-w-sm flex-col items-center gap-2 text-center", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "empty-header"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
const emptyMediaVariants = cva({
  base: "mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  variants: {
    variant: {
      default: "bg-transparent"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
type EmptyMediaVariant = "default" | "icon";
interface EmptyMediaProps extends Omit<useRender.ComponentProps<"div">, "children"> {
  variant?: EmptyMediaVariant;
  children?: React.ReactNode;
}
function EmptyMedia(t0) {
  const $ = _c(17);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  if (variant === "icon") {
    let t2;
    if ($[5] !== className) {
      t2 = cn("mb-2", className);
      $[5] = className;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== children || $[8] !== t2) {
      t3 = <Icon data-slot="empty-icon" className={t2}>{children}</Icon>;
      $[7] = children;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t2;
  if ($[10] !== className || $[11] !== variant) {
    t2 = cn(emptyMediaVariants({
      variant
    }), className);
    $[10] = className;
    $[11] = variant;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== children || $[14] !== props || $[15] !== t2) {
    t3 = <div data-slot="empty-icon" className={t2} {...props}>{children}</div>;
    $[13] = children;
    $[14] = props;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
function EmptyTitle(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("text-lg font-medium tracking-tight", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "empty-title"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
function EmptyDescription(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("text-sm/relaxed text-muted-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "empty-description"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
function EmptyContent(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "empty-content"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
export { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle };